import React, { useContext } from 'react';
import { ToastContext } from 'App';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { observer } from 'mobx-react';
import { UserRoles } from 'enums';
import userStore from 'store/UserStore';
import { Controller, useForm } from 'react-hook-form';
import { Password } from 'primereact/password';
import styled from 'styled-components';
import {
  TOAST_LIFE,
  ERR_NETWORK,
  LOGIN_VALIDATION_RULE,
  PASSWORD_VALIDATION_RULE,
} from 'consts';
import FieldErrorText from 'components/base/field-error-text';
import { LoginPayload } from 'types';
import { LoginContainer, ImageBlock } from './styles';

const MainPage = () => {
  const toast = useContext(ToastContext);

  const defaultValues = { email: '', password: '' };
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({ defaultValues, mode: 'onTouched' });

  const onSubmit = async (dataLogin: LoginPayload) => {
    try {
      await userStore.getAuthData(dataLogin);
      const hasAdminAccess =
        userStore.role === UserRoles.ADMIN ||
        userStore.role === UserRoles.SUPPORT;

      if (!hasAdminAccess) {
        toast?.current?.show({
          severity: 'error',
          detail: `Недостаточно прав у пользователя ${dataLogin.email}!`,
          life: TOAST_LIFE,
        });
      }
    } catch (e: any) {
      toast?.current?.show({
        severity: 'error',
        detail:
          e.code === ERR_NETWORK
            ? 'Отсутствует интернет подключение'
            : 'Неверный логин / пароль',
        life: TOAST_LIFE,
      });
    }
  };

  return (
    <LoginContainer className="bg-primary-400 flex-column">
      <StyledImageBlock>
        <LogoWrapper>
          <LogoText>Sugar-Check</LogoText>
          <LogoSubtitle>Панель администратора</LogoSubtitle>
        </LogoWrapper>
      </StyledImageBlock>
      <StyledCard className="text-center">
        <h3>Авторизация</h3>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-fluid"
        >
          <div className="field">
            <Controller
              name="email"
              control={control}
              rules={LOGIN_VALIDATION_RULE}
              render={({ field: { value, onChange, onBlur }, fieldState }) => (
                <InputText
                  name="email"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  className={`${fieldState.invalid && 'p-invalid'}`}
                  type="text"
                  placeholder="Логин"
                />
              )}
            />
            {errors.email && errors.email.message && (
              <div style={{ textAlign: 'left' }}>
                <FieldErrorText errorText={errors.email.message} />
              </div>
            )}
          </div>
          <div className="field">
            <Controller
              name="password"
              control={control}
              rules={PASSWORD_VALIDATION_RULE}
              render={({ field: { value, onChange, onBlur }, fieldState }) => (
                <Password
                  name="password"
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  className={`${fieldState.invalid && 'p-invalid'}`}
                  placeholder="Пароль"
                  feedback={false}
                  toggleMask
                />
              )}
            />
            <div style={{ textAlign: 'left' }}>
              {errors.password && errors.password.message && (
                <div style={{ textAlign: 'left' }}>
                  <FieldErrorText errorText={errors.password.message} />
                </div>
              )}
            </div>
          </div>

          <Button
            disabled={!isValid}
            type="submit"
            label="Войти"
            className="mt-2"
          />
        </form>
      </StyledCard>
    </LoginContainer>
  );
};

const StyledImageBlock = styled(ImageBlock)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2rem;
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 24px 36px;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
`;

const LogoText = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  text-align: center;
  letter-spacing: 1.5px;
  background: linear-gradient(
    135deg,
    #ffffff 0%,
    rgba(255, 255, 255, 0.7) 50%,
    #ffffff 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  animation: glow 3s ease-in-out infinite alternate;

  @keyframes glow {
    0% {
      filter: drop-shadow(0 0 3px rgba(255, 255, 255, 0.2));
    }
    100% {
      filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.4));
    }
  }
`;

const LogoSubtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  letter-spacing: 0.8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
`;

const StyledCard = styled(Card)`
  width: 90%;
  max-width: 450px;
`;

const MainPageObserver = observer(MainPage);

export { MainPageObserver as MainPage };
